import { getEnvVar } from '@/renderer/util';
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// ----------------------------------------------------------------
// Types
// ----------------------------------------------------------------

type TokenStatus =
  | 'loading'
  | 'ok'
  | 'used'
  | 'expired'
  | 'not_found'
  | 'error';

interface TokenInfo {
  status: TokenStatus;
  token?: string;
  guest_name?: string;
  property_name?: string;
  check_in?: string;
  check_out?: string;
  image_url?: string | null;
}

interface Ratings {
  overall: number;
  cleanliness: number;
  service: number;
  food: number;
}

// ----------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------

const EDGE_FN_URL = `${getEnvVar('SUPABASE_URL')}/functions/v1/submit-guest-review`;

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// ----------------------------------------------------------------
// Star rating widget
// ----------------------------------------------------------------

interface StarRatingProps {
  label: string;
  name: keyof Ratings;
  value: number;
  required?: boolean;
  onChange: (name: keyof Ratings, value: number) => void;
}

const StarRating: React.FC<StarRatingProps> = ({
  label,
  name,
  value,
  required,
  onChange,
}) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="mb-5">
      <p className="mb-2 text-sm font-semibold text-gray-700">
        {label}
        {required ? (
          <span className="ml-1 text-red-500">*</span>
        ) : (
          <span className="ml-1 font-normal text-gray-400">(optional)</span>
        )}
      </p>
      <div className="flex gap-1.5" onMouseLeave={() => setHovered(0)}>
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = n <= (hovered || value);
          return (
            <button
              key={n}
              type="button"
              aria-label={`${n} star${n > 1 ? 's' : ''}`}
              title={`${n} star${n > 1 ? 's' : ''}`}
              className="p-0.5 transition-transform hover:scale-110 focus:outline-none"
              onMouseEnter={() => setHovered(n)}
              onClick={() => onChange(name, value === n ? 0 : n)}
            >
              <svg
                viewBox="0 0 24 24"
                className={`h-9 w-9 transition-colors ${filled ? 'fill-amber-400 stroke-amber-400' : 'fill-gray-200 stroke-gray-200'}`}
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ----------------------------------------------------------------
// State pages
// ----------------------------------------------------------------

const CardShell: React.FC<{
  propertyName: string;
  imageUrl?: string | null;
  children: React.ReactNode;
}> = ({ propertyName, imageUrl, children }) => (
  <div className="flex min-h-screen items-start justify-center bg-gray-100 px-4 py-8 pb-16">
    <div className="w-full max-w-[560px] overflow-hidden rounded-2xl bg-white shadow-lg">
      {imageUrl && (
        <div className="h-44 w-full overflow-hidden">
          <img
            src={imageUrl}
            alt={propertyName}
            className="h-full w-full object-cover"
          />
        </div>
      )}
      <div className="bg-[#1a1a2e] px-8 py-7">
        <h1 className="text-xl font-bold text-white">{propertyName}</h1>
        <p className="mt-1 text-sm text-gray-400">
          We&apos;d love to hear about your stay
        </p>
      </div>
      {children}
    </div>
  </div>
);

const AlreadySubmittedPage: React.FC<{
  propertyName: string;
  imageUrl?: string | null;
}> = ({ propertyName, imageUrl }) => (
  <CardShell propertyName={propertyName} imageUrl={imageUrl}>
    <div className="px-8 py-12 text-center">
      <div className="mb-4 text-5xl">✅</div>
      <h2 className="mb-2 text-lg font-bold text-gray-900">
        Review already submitted
      </h2>
      <p className="text-sm leading-relaxed text-gray-500">
        We&apos;ve already received your review for this stay at{' '}
        <strong>{propertyName}</strong>. Thank you for your feedback!
      </p>
    </div>
  </CardShell>
);

const ExpiredPage: React.FC<{
  propertyName: string;
  imageUrl?: string | null;
}> = ({ propertyName, imageUrl }) => (
  <CardShell propertyName={propertyName} imageUrl={imageUrl}>
    <div className="px-8 py-12 text-center">
      <div className="mb-4 text-5xl">⏳</div>
      <h2 className="mb-2 text-lg font-bold text-gray-900">
        Review link has expired
      </h2>
      <p className="text-sm leading-relaxed text-gray-500">
        Your review link for <strong>{propertyName}</strong> is no longer
        active. Review links are valid for 30 days after checkout. Please
        contact the property if you still wish to leave feedback.
      </p>
    </div>
  </CardShell>
);

const NotFoundPage: React.FC = () => (
  <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4">
    <div className="w-full max-w-[480px] rounded-2xl bg-white px-10 py-12 text-center shadow-lg">
      <div className="mb-4 text-5xl">🔗</div>
      <h2 className="mb-2 text-lg font-bold text-gray-900">
        Invalid review link
      </h2>
      <p className="text-sm leading-relaxed text-gray-500">
        This review link is invalid or could not be found. Please check the link
        in your email and try again.
      </p>
    </div>
  </div>
);

// ----------------------------------------------------------------
// Main page
// ----------------------------------------------------------------

const GuestReviewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [tokenInfo, setTokenInfo] = useState<TokenInfo>({ status: 'loading' });
  const [ratings, setRatings] = useState<Ratings>({
    overall: 0,
    cleanliness: 0,
    service: 0,
    food: 0,
  });
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenInfo({ status: 'not_found' });
      return;
    }

    fetch(`${EDGE_FN_URL}?token=${encodeURIComponent(token)}`)
      .then((res) => res.json())
      .then((data) => setTokenInfo({ ...data }))
      .catch(() => setTokenInfo({ status: 'error' }));
  }, [token]);

  const handleRatingChange = useCallback(
    (name: keyof Ratings, value: number) => {
      setRatings((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (!ratings.overall) {
      setSubmitError('Please rate your overall experience before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          overall_rating: ratings.overall,
          cleanliness_rating: ratings.cleanliness || null,
          service_rating: ratings.service || null,
          food_rating: ratings.food || null,
          remarks: remarks.trim() || null,
        }),
      });

      const data = await res.json();
      if (!res.ok)
        throw new Error(data.error || 'Submission failed. Please try again.');
      setSubmitted(true);
    } catch (err: unknown) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────
  if (tokenInfo.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#1a1a2e]" />
      </div>
    );
  }

  if (tokenInfo.status === 'used') {
    return (
      <AlreadySubmittedPage
        propertyName={tokenInfo.property_name ?? ''}
        imageUrl={tokenInfo.image_url}
      />
    );
  }

  if (tokenInfo.status === 'expired') {
    return (
      <ExpiredPage
        propertyName={tokenInfo.property_name ?? ''}
        imageUrl={tokenInfo.image_url}
      />
    );
  }

  if (tokenInfo.status !== 'ok') {
    return <NotFoundPage />;
  }

  const {
    guest_name: guestName = 'Guest',
    property_name: propertyName = '',
    check_in: checkIn = '',
    check_out: checkOut = '',
    image_url: propertyImageUrl = null,
  } = tokenInfo;

  // ── Thank you ─────────────────────────────────────────────────
  if (submitted) {
    return (
      <CardShell propertyName={propertyName} imageUrl={propertyImageUrl}>
        <div className="px-8 py-12 text-center">
          <div className="mb-4 text-6xl">🌟</div>
          <h2 className="mb-3 text-xl font-bold text-[#1a1a2e]">Thank you!</h2>
          <p className="text-sm leading-relaxed text-gray-600">
            Your review has been submitted. We truly appreciate you taking the
            time to share your experience. It helps us deliver a better stay for
            every guest.
          </p>
        </div>
      </CardShell>
    );
  }

  // ── Review form ───────────────────────────────────────────────
  return (
    <CardShell propertyName={propertyName} imageUrl={propertyImageUrl}>
      {/* Stay info bar */}
      <div className="flex flex-wrap gap-6 border-b border-gray-100 bg-gray-50 px-8 py-4">
        <div>
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            Guest
          </span>
          <span className="text-sm text-gray-700">{guestName}</span>
        </div>
        {checkIn && (
          <div>
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Check-in
            </span>
            <span className="text-sm text-gray-700">{fmtDate(checkIn)}</span>
          </div>
        )}
        {checkOut && (
          <div>
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Check-out
            </span>
            <span className="text-sm text-gray-700">{fmtDate(checkOut)}</span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="px-8 py-7">
        <p className="mb-6 text-sm leading-relaxed text-gray-600">
          Dear <strong className="text-[#1a1a2e]">{guestName}</strong>, thank
          you for staying with us at{' '}
          <strong className="text-[#1a1a2e]">{propertyName}</strong>. Your
          feedback helps us serve future guests better. The form takes less than
          a minute.
        </p>

        <StarRating
          label="Overall experience"
          name="overall"
          value={ratings.overall}
          required
          onChange={handleRatingChange}
        />
        <StarRating
          label="Room cleanliness"
          name="cleanliness"
          value={ratings.cleanliness}
          onChange={handleRatingChange}
        />
        <StarRating
          label="Service quality"
          name="service"
          value={ratings.service}
          onChange={handleRatingChange}
        />
        <StarRating
          label="Food & beverage"
          name="food"
          value={ratings.food}
          onChange={handleRatingChange}
        />

        <hr className="my-5 border-gray-100" />

        <div className="mb-6">
          <label
            htmlFor="remarks"
            className="mb-2 block text-sm font-semibold text-gray-700"
          >
            Comments &amp; suggestions
            <span className="ml-1 font-normal text-gray-400">(optional)</span>
          </label>
          <textarea
            id="remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            maxLength={2000}
            placeholder="Tell us what we did well or where we can improve…"
            className="w-full resize-y rounded-xl border border-gray-200 px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            rows={4}
          />
        </div>

        {submitError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {submitError}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-xl bg-[#1a1a2e] py-3.5 text-sm font-semibold text-white transition hover:bg-[#2d2d54] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Submitting…' : 'Submit my review'}
        </button>
      </form>

      <div className="border-t border-gray-100 px-8 py-4 text-center text-xs text-gray-400">
        This review link is personal and expires 30 days after checkout.
      </div>
    </CardShell>
  );
};

export default GuestReviewPage;
